import { computed, type ComputedRef } from 'vue';

/**
 * Supported aspect ratio types for responsive media components
 */
export type AspectRatio = 'dslr' | 'square' | 'widescreen' | 'ultra-wide' | 'auto';

/**
 * Composable for handling aspect ratio logic in responsive media components
 * 
 * @param aspectRatio - The aspect ratio configuration, either a reactive ref or static value
 * @returns Object containing the computed CSS class for the aspect ratio
 * 
 * @example
 * ```typescript
 * // In a component with a prop
 * const props = defineProps({
 *   aspectRatio: {
 *     type: String as PropType<AspectRatio>,
 *     default: 'auto'
 *   }
 * });
 * 
 * const { getAspectRatio } = useAspectRatio(() => props.aspectRatio);
 * ```
 * 
 * @example
 * ```typescript
 * // With a static value
 * const { getAspectRatio } = useAspectRatio('widescreen');
 * ```
 */
export function useAspectRatio(aspectRatio: (() => AspectRatio) | AspectRatio) {
  const getAspectRatio: ComputedRef<string> = computed(() => {
    const ratio = typeof aspectRatio === 'function' ? aspectRatio() : aspectRatio;
    
    switch (ratio) {
      case 'dslr': // Landscape 3:2
        return 'aspect-[3/2]';
      case 'square': // Square 1:1
        return 'aspect-square';
      case 'widescreen': // Widescreen 16:9
        return 'aspect-video';
      case 'ultra-wide': // Ultra-wide 21:9
        return 'aspect-[21/9]';
      case 'auto':
      default:
        return 'w-auto';
    }
  });

  return {
    getAspectRatio
  };
}